// Thin wrapper around the GTM dataLayer for GA4 ecommerce funnel events.
//
// Value convention (Option D):
//   - GA4 `value` is ALWAYS the gross customer price (what the customer pays).
//     This keeps the whole funnel reconciling: view_item_list -> add_to_cart ->
//     begin_checkout -> add_payment_info -> purchase all use the same scale.
//   - `commission` (Sukuto's margin) is attached ONLY where it is genuinely
//     known and accurate — checkout / payment (partial-eligible carts) and
//     purchase. It is never fabricated.
//   - Google Ads keeps reading the commission field, so bidding is unchanged.
//
// Pattern matches the existing `window.dataLayer.push` usage in the codebase;
// no new analytics library is introduced.

const push = (payload) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

// Normalize an Indian mobile number to E.164 (+91XXXXXXXXXX) for Google Ads
// enhanced conversions — Google discards phone numbers without a country code,
// so a bare 10-digit `user.mobile` would silently contribute nothing to match
// rates. Backend stores digits only, with or without the 91 prefix.
export const toE164IndianPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return `+${digits}`; // already has some country code — trust it
};

// Map a vehicle-shaped object (search card, cart row, or booking vehicle) to a
// GA4 item. `price` is the gross customer price for that line.
export const toGa4Item = (row = {}, price = 0, index = 0) => {
  const v = row.vehicle || row;
  return {
    item_id: String(v.id ?? `vehicle_${index}`),
    item_name: [v.company, v.name].filter(Boolean).join(" ") || "Vehicle",
    item_brand: v.company || undefined,
    item_category: v.fuelType || undefined,
    item_variant: v.transmissionType || undefined,
    price: Number(price) || 0,
    quantity: Number(row.count) || 1,
  };
};

export const trackSearch = ({ city, startDate, endDate, vehicleType, resultCount } = {}) => {
  push({
    event: "search",
    search_term: city || "",
    search_city: city || "",
    search_start: startDate || "",
    search_end: endDate || "",
    search_vehicle_types: Array.isArray(vehicleType) ? vehicleType.join(",") : "",
    result_count: Number(resultCount) || 0,
  });
};

// The lead popup had no events at all, so its dismissal rate - the whole cost
// side of it - was invisible. stage: shown | dismissed | submitted.
export const trackLeadPopup = (stage, context = "") => {
  push({ event: "lead_popup", lead_popup_stage: stage, lead_popup_context: context || undefined });
};

export const trackViewItemList = (items, itemListName = "Search Results") => {
  if (!items || items.length === 0) return;
  push({ ecommerce: null });
  push({
    event: "view_item_list",
    ecommerce: { item_list_name: itemListName, currency: "INR", items },
  });
};

export const trackAddToCart = (item, value) => {
  if (!item) return;
  push({ ecommerce: null });
  push({
    event: "add_to_cart",
    ecommerce: {
      currency: "INR",
      value: Number(value) || Number(item.price) || 0,
      items: [item],
    },
  });
};

export const trackBeginCheckout = ({ items, value, commission } = {}) => {
  if (!items || items.length === 0) return;
  push({ ecommerce: null });
  push({
    event: "begin_checkout",
    ecommerce: { currency: "INR", value: Number(value) || 0, items },
    // Only present when it is the real commission (omit otherwise).
    ...(commission != null ? { commission: Number(commission) } : {}),
  });
};

export const trackAddPaymentInfo = ({ items, value, commission, paymentType } = {}) => {
  push({ ecommerce: null });
  push({
    event: "add_payment_info",
    ecommerce: {
      currency: "INR",
      value: Number(value) || 0,
      payment_type: String(paymentType ?? ""),
      items: items || [],
    },
    ...(commission != null ? { commission: Number(commission) } : {}),
  });
};
